import Vue from "vue";

export const SubscriptionState = {
  currentSubscription: {},
  subscription: null,
  subscriptions: [],
  totalSubscriptions: 0
};

export const SubscriptionGetters = {
  currentSubscription: state => {
    return state.currentSubscription;
  },
  subscription: state => {
    return state.subscription;
  },
  subscriptions: state => {
    return state.subscriptions;
  },
  totalSubscriptions: state => {
    return state.totalSubscriptions;
  }
};

export const SubscriptionMutations = {
  setCurrentSubscription: (state, data) => {
    Vue.set(state, "currentSubscription", data);
  },
  setSubscription: (state, data) => {
    if (data) {
      const index = state.subscriptions.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.subscriptions];
        arr[index] = data;
        Vue.set(state, "subscriptions", arr);
      }
    }
    Vue.set(state, "subscription", data);
  },
  setSubscriptions: (state, data) => {
    Vue.set(state, "subscriptions", data.subscriptions);
    Vue.set(state, "totalSubscriptions", data.total_count);
  },
  addSubscription: (state, data) => {
    Vue.set(state, "subscriptions", [data, ...state.subscriptions]);
    Vue.set(state, "totalSubscriptions", state.totalSubscriptions + 1);
  },
  removeSubscription: (state, data) => {
    const arr = state.subscriptions.filter(
      i => Number(i.id) !== Number(data.id)
    );
    Vue.set(state, "subscriptions", arr);
    Vue.set(state, "totalSubscriptions", state.totalSubscriptions - 1);
  }
};

export const SubscriptionActions = {
  getCurrentSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/current_subscription`)
      .then(response => {
        commit("setCurrentSubscription", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch current subscription from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getSubscriptions: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/centers/${data.center_id}/subscriptions${params}`)
      .then(response => {
        commit("setSubscriptions", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch subscriptions from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/centers/${data.center_id}/subscriptions/${data.id}`)
      .then(response => {
        commit("setSubscription", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch subscription from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/centers/${data.center_id}/subscriptions`, {
        subscription: data.subscription,
        current_subscription_id: data.current_subscription_id // we get current subscription id from the error earlier
      })
      .then(response => {
        commit("addSubscription", response.data);
        const title = "New subscription created";
        const message = "Success! A new subscription has been created.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data, message };
      })
      .catch(error => {
        /**
        {
          error: {
            message: "This subscription period overlaps with the current active period for.",
            current_subscription: {
              id: 4,
              name: "Billing custom - December",
              amount: 2000000.0,
              number_of_seats: 10,
              start_date: "2024-12-01",
              expiration_date: "2024-12-31",
              status: "active",
              type: "Subscription::Custom"
            }
          }
        }
        */
        const err = error.response && error.response.data;
        const errorMessage = (err.error && err.error.message) || "";
        const errorData = (err.error && err.error.current_subscription) || {};
        const message = errorMessage || "Unable to create subscription.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: errorData, message };
      });
  },
  updateSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/centers/${data.center_id}/subscriptions/${data.id}`, {
        subscription: data.subscription
      })
      .then(response => {
        commit("setSubscription", response.data);
        const title = data.title || "Subscription updated";
        const message =
          data.message || "Success! The subscription has been updated.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data, message };
      })
      .catch(error => {
        /**
        {
          error: {
            message: "This subscription period overlaps with the current active period for.",
            current_subscription: {
              id: 4,
              name: "Billing custom - December",
              amount: 2000000.0,
              number_of_seats: 10,
              start_date: "2024-12-01",
              expiration_date: "2024-12-31",
              status: "active",
              type: "Subscription::Custom"
            }
          }
        }
        */
        const err = error.response && error.response.data;
        const errorMessage = (err.error && err.error.message) || "";
        const errorData = (err.error && err.error.current_subscription) || {};
        const message = errorMessage || "Unable to update subscription.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: errorData, message };
      });
  },
  cancelSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(
        `/api/v1/centers/${data.center_id}/subscriptions/${data.id}/cancel`,
        {
          subscription: data.subscription
        }
      )
      .then(response => {
        commit("setSubscription", response.data);
        const title = data.title || "Subscription cancelled";
        const message =
          data.message || "Success! The subscription has been cancelled.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to cancel subscription.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteSubscription: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/centers/${data.center_id}/subscriptions/${data.id}`)
      .then(response => {
        commit("removeSubscription", data);
        const title = "Subscription deleted";
        const message = `Success! Subscription has been deleted.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete subscription.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  }
};

// PATCH /api/v1/centers/:center_id/subscriptions/:id
/**
 * When update active subscription
{
  "subscription": {
    "name": "Custom subscription 2",
    "amount": 3000000,
    "number_of_seats": 8,
    "start_date": "2024-11-10",
    "expiration_date": "2024-12-10",
    "notes": "This is custom subscription 2"
  }
}
 */
/**
 * When update scheduled subscription
{
  "subscription": {
    "name": "Custom subscription 2",
    "amount": 3000000,
    "number_of_seats": 8,
    "start_date": "2024-11-10",
    "expiration_date": "2024-12-10",
    "notes": "This is custom subscription 2",
    "accessible_features": {
      "branches": true, "analytics": true, "sales_pipelines": false,
      "availabilities_and_appointments": true
    }
  }
}
 */
/**
 * When update inactive subscription
{
  "subscription": {
    "cancellation_reason": "I don't want to renew this subscription."
  }
}
 */

// PATCH /api/v1/centers/:center_id/subscriptions/:id/cancel
/**
{
  subscription: {
    cancellation_reason: string | null
  }
}
 */



// WEBPACK FOOTER //
// ./src/store/modules/subscription.store.js